import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Code, FileText, RotateCcw, HelpCircle, Plus, Trash2, Check, X } from 'lucide-react'
import Editor from '@monaco-editor/react'
import { APIEndpoint } from '../../stores/apiStore'
import { useTheme } from '../../hooks/useTheme'
import EmptyStateIllustration from '../ui/EmptyStateIllustration'

interface RequestBodyEditorProps {
  endpoint: APIEndpoint | null
  body: any
  onBodyChange: (body: any) => void
  viewMode: 'form' | 'json'
  onViewModeChange: (mode: 'form' | 'json') => void
}

const RequestBodyEditor: React.FC<RequestBodyEditorProps> = ({
  endpoint,
  body,
  onBodyChange,
  viewMode,
  onViewModeChange
}) => {
  const [jsonText, setJsonText] = useState('')
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [enabledParams, setEnabledParams] = useState<Set<string>>(new Set())
  const [customParams, setCustomParams] = useState<Array<{name: string, value: any, type: string}>>([])
  const [newParamName, setNewParamName] = useState('')
  const [isAddingParam, setIsAddingParam] = useState(false)
  const isDark = useTheme()

  useEffect(() => {
    if (body) {
      setJsonText(JSON.stringify(body, null, 2))
      setFormData(body)
      // Sync enabled parameters based on body content
      if (endpoint?.parameters) {
        const newEnabledParams = new Set<string>()
        endpoint.parameters.forEach(param => {
          if (param.required || (body && body.hasOwnProperty(param.name))) {
            newEnabledParams.add(param.name)
          }
        })
        setEnabledParams(newEnabledParams)
      }
    }
  }, [body, endpoint])

  // Sync form data to JSON when form changes
  useEffect(() => {
    if (viewMode === 'form' && Object.keys(formData).length > 0) {
      setJsonText(JSON.stringify(formData, null, 2))
    }
  }, [formData, viewMode])

  // Sync JSON to form data when JSON changes and is valid
  useEffect(() => {
    if (jsonText && !jsonError) {
      try {
        const parsed = JSON.parse(jsonText)
        setFormData(parsed)
        
        // Update enabled parameters based on JSON content
        if (endpoint?.parameters) {
          const newEnabledParams = new Set<string>()
          endpoint.parameters.forEach(param => {
            if (param.required || (parsed && parsed.hasOwnProperty(param.name))) {
              newEnabledParams.add(param.name)
            }
          })
          
          // Also check for custom parameters (parameters not defined in endpoint schema)
          Object.keys(parsed).forEach(key => {
            const isEndpointParam = endpoint.parameters?.some(param => param.name === key)
            if (!isEndpointParam) {
              // This is a custom parameter, add it to custom params if not already there
              const existingCustomParam = customParams.find(cp => cp.name === key)
              if (!existingCustomParam) {
                const newCustomParam = {
                  name: key,
                  value: parsed[key],
                  type: typeof parsed[key] === 'number' ? 'number' : typeof parsed[key] === 'object' ? 'object' : 'text'
                }
                setCustomParams(prev => [...prev, newCustomParam])
              }
            } else {
              newEnabledParams.add(key)
            }
          })
          
          setEnabledParams(newEnabledParams)
        }
      } catch {
        // Invalid JSON, don't sync
      }
    }
  }, [jsonText, jsonError, endpoint])

  useEffect(() => {
    // Initialize enabled parameters when endpoint changes
    if (endpoint?.parameters) {
      const initialEnabled = new Set<string>()
      endpoint.parameters.forEach(param => {
        if (param.required) {
          initialEnabled.add(param.name)
        }
      })
      setEnabledParams(initialEnabled)
    }
  }, [endpoint])

  // Handle view mode changes and ensure proper sync
  useEffect(() => {
    if (viewMode === 'form' && formData && Object.keys(formData).length > 0) {
      // When switching to form view, update JSON to reflect current form data
      setJsonText(JSON.stringify(formData, null, 2))
    } else if (viewMode === 'json' && jsonText) {
      // When switching to JSON view, try to sync enabled params with JSON content
      try {
        const parsed = JSON.parse(jsonText)
        if (endpoint?.parameters) {
          const newEnabledParams = new Set<string>()
          endpoint.parameters.forEach(param => {
            if (param.required || (parsed && parsed.hasOwnProperty(param.name))) {
              newEnabledParams.add(param.name)
            }
          })
          setEnabledParams(newEnabledParams)
        }
      } catch {
        // Invalid JSON, keep current state
      }
    }
  }, [viewMode])

  const handleJsonChange = (text: string) => {
    setJsonText(text)
    try {
      const parsed = JSON.parse(text)
      setJsonError(null)
      setFormData(parsed) // Sync to form data
      onBodyChange(parsed)
      
      // Update enabled parameters based on JSON content
      if (endpoint?.parameters) {
        const newEnabledParams = new Set<string>()
        const newCustomParams: Array<{name: string, value: any, type: string}> = []
        
        // Check each property in the parsed JSON
        Object.keys(parsed).forEach(key => {
          const isEndpointParam = endpoint.parameters?.some(param => param.name === key)
          if (isEndpointParam) {
            newEnabledParams.add(key)
          } else {
            // This is a custom parameter
            const value = parsed[key]
            const type = typeof value === 'number' ? 'number' : typeof value === 'object' ? 'object' : 'text'
            newCustomParams.push({ name: key, value, type })
          }
        })
        
        // Add required parameters even if not in JSON
        endpoint.parameters.forEach(param => {
          if (param.required) {
            newEnabledParams.add(param.name)
          }
        })
        
        setEnabledParams(newEnabledParams)
        setCustomParams(newCustomParams)
      }
    } catch (error) {
      setJsonError('Invalid JSON')
    }
  }

  const handleFormChange = (key: string, value: any) => {
    const newFormData = { ...formData, [key]: value }
    setFormData(newFormData)
    // Also update the parent component's body state
    onBodyChange(newFormData)
  }

  const handleParamToggle = (paramName: string, enabled: boolean) => {
    const newEnabledParams = new Set(enabledParams)
    if (enabled) {
      newEnabledParams.add(paramName)
      // Add to formData with empty value if not present
      if (!formData.hasOwnProperty(paramName)) {
        const param = endpoint?.parameters?.find(p => p.name === paramName)
        const defaultValue = param?.type === 'number' ? 0 : param?.type === 'object' ? {} : ''
        const newFormData = { ...formData, [paramName]: defaultValue }
        setFormData(newFormData)
        onBodyChange(newFormData)
      }
    } else {
      newEnabledParams.delete(paramName)
      // Remove from formData when disabled
      const newFormData = { ...formData }
      delete newFormData[paramName]
      setFormData(newFormData)
      onBodyChange(newFormData)
    }
    setEnabledParams(newEnabledParams)
  }

  const handleDeleteCustomParam = (index: number) => {
    const param = customParams[index]
    const newCustomParams = customParams.filter((_, i) => i !== index)
    setCustomParams(newCustomParams)
    
    // Remove from formData
    const newFormData = { ...formData }
    delete newFormData[param.name]
    setFormData(newFormData)
    onBodyChange(newFormData)
  }

  const handleAddCustomParam = () => {
    if (newParamName.trim()) {
      const newParam = {
        name: newParamName.trim(),
        value: '',
        type: 'text'  // Always treat new parameters as text
      }
      setCustomParams([...customParams, newParam])
      setNewParamName('')
      setIsAddingParam(false)
    }
  }

  const handleCustomParamChange = (index: number, value: any) => {
    const param = customParams[index]
    const newFormData = { ...formData, [param.name]: value }
    setFormData(newFormData)
    onBodyChange(newFormData)
  }

  const handleLoadSample = () => {
    if (endpoint?.samplePayload) {
      onBodyChange(endpoint.samplePayload)
      
      // Update enabled parameters based on sample payload
      if (endpoint?.parameters) {
        const newEnabledParams = new Set<string>()
        endpoint.parameters.forEach(param => {
          if (param.required || (endpoint.samplePayload && endpoint.samplePayload.hasOwnProperty(param.name))) {
            newEnabledParams.add(param.name)
          }
        })
        setEnabledParams(newEnabledParams)
      }
    }
  }

  const handleClearBody = () => {
    onBodyChange(null)
    setJsonText('')
    setFormData({})
    // Reset enabled params to only required ones
    if (endpoint?.parameters) {
      const requiredParams = new Set<string>()
      endpoint.parameters.forEach(param => {
        if (param.required) {
          requiredParams.add(param.name)
        }
      })
      setEnabledParams(requiredParams)
    } else {
      setEnabledParams(new Set())
    }
    setCustomParams([])
  }

  const renderFormView = () => {
    if (!endpoint) {
      return (
        <EmptyStateIllustration
          type="body"
          title="No Endpoint Selected"
          description="Please select an endpoint from the sidebar to configure the request body parameters and structure."
        />
      )
    }

    return (
      <div className="space-y-3">
        {/* Default Parameters */}
        {endpoint.parameters?.map((param) => {
          const isEnabled = enabledParams.has(param.name) || param.required
          return (
            <div key={param.name} className={`flex items-center space-x-3 transition-opacity duration-200 ${!isEnabled ? 'opacity-50' : ''}`}>
              {/* Enhanced Checkbox for enable/disable */}
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  disabled={param.required}
                  onChange={(e) => handleParamToggle(param.name, e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => !param.required && handleParamToggle(param.name, !isEnabled)}
                  className={`w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center cursor-pointer
                    ${param.required 
                      ? 'bg-primary-100 border-primary-300 cursor-not-allowed' 
                      : isEnabled 
                        ? 'bg-primary-600 border-primary-600 hover:bg-primary-700 hover:border-primary-700' 
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-gray-600'
                    }
                    ${!isEnabled && !param.required ? 'shadow-sm' : ''}
                  `}
                  title={param.required ? 'Required parameter' : isEnabled ? 'Click to disable parameter' : 'Click to enable parameter'}
                >
                  {isEnabled && (
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  )}
                  {!isEnabled && !param.required && (
                    <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full opacity-40"></div>
                  )}
                </div>
              </div>
              
              {/* Parameter Name/Label */}
              <div className="flex items-center space-x-2 w-32 flex-shrink-0">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {param.name}
                  {param.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {param.description && (
                  <div 
                    className="relative group cursor-help"
                    title={param.description}
                  >
                    <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0" />
                    <div className="absolute left-0 top-5 z-50 w-64 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      {param.description}
                      <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Input Field */}
              <div className="flex-1 max-w-xs">
                {param.type === 'object' ? (
                  <textarea
                    disabled={!isEnabled}
                    value={formData[param.name] ? JSON.stringify(formData[param.name], null, 2) : ''}
                    onChange={(e) => {
                      if (!isEnabled) return
                      try {
                        const parsed = JSON.parse(e.target.value)
                        handleFormChange(param.name, parsed)
                      } catch {
                        // Keep as string for now
                      }
                    }}
                    rows={2}
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-mono disabled:bg-gray-100 dark:disabled:bg-gray-800"
                    placeholder={JSON.stringify(param.example, null, 2)}
                  />
                ) : (
                  <input
                    type={param.type === 'number' ? 'number' : 'text'}
                    disabled={!isEnabled}
                    value={formData[param.name] || ''}
                    onChange={(e) => isEnabled && handleFormChange(param.name, e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs disabled:bg-gray-100 dark:disabled:bg-gray-800"
                    placeholder={param.example?.toString() || ''}
                  />
                )}
              </div>
            </div>
          )
        })}

        {/* Custom Parameters */}
        {customParams.map((param, index) => (
          <div key={`custom-${index}`} className="flex items-center space-x-3">
            {/* Delete button */}
            <button
              onClick={() => handleDeleteCustomParam(index)}
              className="w-4 h-4 text-red-500 hover:text-red-700 transition-colors"
              title="Delete parameter"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            
            {/* Parameter Name (editable) */}
            <div className="w-32 flex-shrink-0">
              <input
                type="text"
                value={param.name}
                onChange={(e) => {
                  const newCustomParams = [...customParams]
                  newCustomParams[index] = { ...param, name: e.target.value }
                  setCustomParams(newCustomParams)
                }}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-medium"
                placeholder="Parameter name"
              />
            </div>
            
            {/* Input Field */}
            <div className="flex-1 max-w-xs">
              <input
                type="text"
                value={formData[param.name] || ''}
                onChange={(e) => handleCustomParamChange(index, e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                placeholder="Parameter value"
              />
            </div>
          </div>
        ))}

        {/* Add New Parameter */}
        {isAddingParam ? (
          <div className="flex items-center space-x-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-2">
            <div className="w-4 h-4"></div> {/* Spacer for alignment */}
            
            {/* Parameter Name Input */}
            <div className="w-32 flex-shrink-0">
              <input
                type="text"
                value={newParamName}
                onChange={(e) => setNewParamName(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                placeholder="Parameter name"
                autoFocus
              />
            </div>
            
            {/* Parameter Value Input */}
            <div className="flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Parameter value"
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-1">
              <button
                onClick={handleAddCustomParam}
                className="p-1 text-green-600 hover:text-green-800 transition-colors"
                title="Add parameter"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setIsAddingParam(false)
                  setNewParamName('')
                }}
                className="p-1 text-red-600 hover:text-red-800 transition-colors"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsAddingParam(true)}
            className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Custom Parameter</span>
          </button>
        )}
      </div>
    )
  }

  const renderJsonView = () => {
    if (!endpoint) {
      return (
        <EmptyStateIllustration
          type="body"
          title="No Endpoint Selected"
          description="Please select an endpoint from the sidebar to configure the request body in JSON format."
        />
      )
    }

    return (
      <div className="space-y-4">
        <div className="relative">
          <div className="h-64 border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <Editor
              height="100%"
              language="json"
              theme={isDark ? "vs-dark" : "light"}
              value={jsonText}
              onChange={(value) => value !== undefined && handleJsonChange(value)}
              options={{
                minimap: { enabled: false },
                lineNumbers: 'on',
                readOnly: false,
                fontSize: 14,
                wordWrap: 'on',
                automaticLayout: true,
                scrollBeyondLastLine: false,
                folding: true,
                bracketPairColorization: { enabled: true }
              }}
            />
          </div>
          {jsonError && (
            <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
              {jsonError}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-1">
            <button
              onClick={() => onViewModeChange('form')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'form'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-1" />
              Form
            </button>
            <button
              onClick={() => onViewModeChange('json')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'json'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Code className="w-4 h-4 inline mr-1" />
              JSON
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {endpoint?.samplePayload && (
            <button
              onClick={handleLoadSample}
              className="px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
            >
              Load Sample
            </button>
          )}
          <button
            onClick={handleClearBody}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body Editor */}
      <motion.div
        key={viewMode}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {viewMode === 'form' ? renderFormView() : renderJsonView()}
      </motion.div>
    </div>
  )
}

export default RequestBodyEditor
